import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { GetOrganizationsDocument, type GetOrganizationsQuery } from '@/generated/root-types';
import { DataTable } from '@gastrosoft/shared/ui';
import { CustomerInviteButton } from '@/components/customer-invite/CustomerInviteDialog';

interface OrganizationRow {
  id: string;
  name: string;
  customerNumber: string | number;
  mandant: string;
  resellerId: string | number;
  createdAt: string;
  [key: string]: unknown;
}

export default function Customers() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('rowsPerPage');
    return saved ? parseInt(saved, 10) : 25;
  });

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading } = useQuery<GetOrganizationsQuery>(GetOrganizationsDocument, {
    variables: { first: 50 },
  });

  const organizations = data?.organizations?.nodes ?? [];
  const totalCount = data?.organizations?.totalCount ?? 0;

  const rows: OrganizationRow[] = organizations.map((o) => ({
    id: o.id,
    name: o.name,
    customerNumber: o.customerNumber ?? '–',
    mandant: o.tenant?.name ?? '–',
    resellerId: o.tenant?.resellerId ?? '–',
    createdAt: formatDate(o.createdAt),
  }));

  const columns: GridColDef[] = [
    { field: 'customerNumber', headerName: t('customers.customerNumber'), width: 120 },
    { field: 'name', headerName: t('customers.customerOrg'), flex: 1, minWidth: 200 },
    { field: 'mandant', headerName: t('common.tenant'), flex: 1, minWidth: 160 },

    { field: 'resellerId', headerName: t('common.resellerId'), width: 120 },
    { field: 'createdAt', headerName: t('common.created'), width: 140 },
  ];

  const handleRowClick = (row: OrganizationRow) => {
    navigate(`/customers/${row.id}`);
  };

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{t('customers.title')}</Typography>
        <CustomerInviteButton />
      </Box>
      <DataTable<OrganizationRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={handleRowClick}
        paginationMode="client"
      />
    </Box>
  );
}
